/**
 * Agent-level permissions, modelled after the Spaces `SpaceAction` pattern.
 *
 * Ownership is the single dimension: the person who created an agent is its
 * owner. Non-owners may view (and fork via duplicate) an agent, but never edit
 * or delete it, and it does not appear for them in the chat agent picker.
 * System agents are read-only for everyone but usable by everyone in chat.
 */
export type AgentAction = 'view' | 'edit' | 'delete' | 'duplicate' | 'chat_use';

export interface AgentPermissionContext {
  /** Whether the current user owns the agent (`agent.is_owner`). */
  isOwner: boolean;
  /** Whether the agent is a built-in system agent (`agent.is_system`). */
  isSystem: boolean;
  /** Whether the agent is private (`agent.private`). */
  isPrivate: boolean;
}

export function canAgent(action: AgentAction, ctx: AgentPermissionContext): boolean {
  switch (action) {
    case 'view':
    case 'duplicate':
    case 'chat_use':
      // A private agent is visible/usable only to its owner. System agents are
      // available to everyone, and shared (non-private) agents to everyone.
      return ctx.isSystem || ctx.isOwner || !ctx.isPrivate;
    case 'edit':
    case 'delete':
      // Only the owner can mutate an agent, and system agents are read-only.
      return ctx.isOwner && !ctx.isSystem;
    default:
      return false;
  }
}

/** Convenience: build the permission context straight from an agent-like object. */
export function agentPermissionContext(agent: {
  is_owner: boolean;
  is_system: boolean;
  private: boolean;
}): AgentPermissionContext {
  return { isOwner: agent.is_owner, isSystem: agent.is_system, isPrivate: agent.private };
}
